const ALLOWED_GROUPS = new Set(['1', '2', '4']);

function getCookie(name: string): string | null {
	if (typeof document === 'undefined') return null;
	const pattern = new RegExp(String.raw`(^|;)\s*${name}=([^;]*)`);
	const match = pattern.exec(document.cookie);
	return match ? decodeURIComponent(match[2]) : null;
}

export type ConsentGroups = Record<string, boolean>;

/**
 * Parses the OneTrust `OptanonConsent` cookie and returns a map of
 * consent-group ID to granted (true/false).
 *
 * Groups: 1 = Strictly Necessary, 2 = Performance, 4 = Targeting.
 *
 * Falls back to performance-denied defaults when the cookie is missing
 * or when Global Privacy Control is enabled without explicit consent.
 */
export function getConsentGroups(): ConsentGroups {
	const defaults: ConsentGroups = { '1': true, '2': false, '4': false };

	const optanonConsent = getCookie('OptanonConsent');
	const match = optanonConsent?.match(/groups=([^&;]*)/);
	const groupsValue = match?.[1];

	if (!groupsValue) return defaults;

	const groups: ConsentGroups = {};
	for (const entry of groupsValue.split(',')) {
		const [id, value] = entry.split(':');
		if (id && ALLOWED_GROUPS.has(id) && (value === '0' || value === '1')) {
			groups[id] = value === '1';
		}
	}

	return Object.keys(groups).length > 0 ? groups : defaults;
}

/** Returns true when the user has granted Performance (category 2) consent. */
export function hasPerformanceConsent(): boolean {
	return getConsentGroups()['2'] === true;
}

/** Returns true when the user has granted Targeting (category 4) consent. */
export function hasTargetingConsent(): boolean {
	return getConsentGroups()['4'] === true;
}
